///include /assets/js/app/20i/shared-vps-optimisation-specs.js
///include /assets/js/app/p/server-location-utils.js

/** Class to assist with cloud products JSON schema (20i Global) */
class CloudProductHelper {
    /**
     * @type {CloudProductHelper}
     */
    static get inst() {
        if(!this._inst) {
            this._inst = new CloudProductHelper({})
            this._inst.loadConfig();
        }
        return this._inst
    }

    /**
     * Load the returned cloud product data JSON file into the class
     *
     * @param {Object} config
     * @param {?Object} brand
     */
    constructor(config, brand = null) {
        this.brand              = brand;
        this.config             = config.productData
        this.discountData       = config.discountData
        this.settings           = config.settings
        this.base               = null
        this.lowestPriceSBCache = null;
    }

    async loadConfig() {
        try {
            var all = await $.ajax(`/a/catalogue/cloudProducts?all=true`);
        } catch (e) {
            // Do nothing?
        }
        var cloudProducts = all.cloudProducts;

        if (cloudProducts) {
            this.config       = cloudProducts.productData;
            this.settings     = cloudProducts.settings;
        }

        this.base         = all.base;
    }

    /**
     * Return an array of the available cloud providers
     *
     * @param {boolean} bypassSettings If true, return all cloud providers
     * ignoring any excluded in settings
     * @returns {Array} Array of cloud provider codes e.g. 'aws'
     */
    getCloudProviders(bypassSettings = false) {
        const clouds = Object.keys(this.config)

        if (bypassSettings) {
            return clouds
        }

        let enabledClouds = []

        clouds.forEach(cloud => {
            if (
                (this.cloudProductOptions(cloud, true)).length !==
                this.settings.discontinuedProducts[cloud].length
            ) {
                if ((this.cloudProductOptions(cloud)).length) {
                    enabledClouds.push(cloud)
                }
            }
        })

        return enabledClouds
    }

    /**
     * Read the settings object in order to determine which optimisation
     * profiles are available for display on the page.
     *
     * @param {boolean} bypassSettings If true, return all optimisation profiles
     * ignoring any excluded in settings
     * @returns {Object} Optimisation Profiles to display
     */
    getDisplayOptimisationProfiles(bypassSettings = false) {
        if (bypassSettings) {
            return this.optimisationProfiles
        }

        if (this.settings.excludedOptimisationProfiles.length) {
            const displayOptimisationProfiles = {}
            for (const key in this.optimisationProfiles) {
                if (
                    !this.settings.excludedOptimisationProfiles.includes(
                        this.optimisationProfiles[key].id
                    )
                ) {
                    displayOptimisationProfiles[key] = this.optimisationProfiles[key]
                }
            }

            return displayOptimisationProfiles
        }

        return this.optimisationProfiles
    }

    /**
     * Read the cloud product settings JSON in order to determine
     * which optimisation profile ID's are excluded from availability.
     *
     * @returns {Array} Available optimisation profile ID's
     */
    get excludedOptimisationProfiles() {
        if ('excludedOptimisationProfiles' in this.settings) {
            return this.settings.excludedOptimisationProfiles
        }

        return []
    }

    /**
     * @returns {Object} VPS Optimisation spec objects
     */
    get optimisationProfiles() {
        return VPS_OPTIMISATION_SPEC
    }

    /**
     * Return the Reseller discount from the settings JSON.
     *
     * @returns {number}
     */
    get resellerDiscount() {
        return this.settings?.resellerDiscount
    }

    /**
     * Get the cost of TL Backups Pro as a percentage of the server cost. Stored
     * in the settings JSON.
     *
     * @returns {number}
     */
    get timelineBackupsProPercent() {
        return this.settings.timelinePercentPrice
    }

    /**
     * Available specs as per basketProductOptions
     *
     * @returns {string[]} Specnames
     */
    static specNames() {
        return [
            "bandwidth",
            "country",
            "cpu",
            "disk",
            "provider",
            "ram",
            "zone",
        ]
    }

    /**
     * Returns the estimated bandwidth monthly cost based on provider &
     * currency.
     *
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The server zone/location e.g. 'us-east-1'
     * @param {string} provider
     * @param {number} bandwidthEstimate
     * @param {string} currency
     * @returns {string}
     */
    additionalBandwidthPrice(
        spec,
        zone,
        provider,
        bandwidthEstimate,
        currency
    ) {
        const product = this.config[provider][spec]
        const availableZones = Object.keys(product.detail.bandwidthAdditionalByZone)
        const foundZone = availableZones.find(z => z == zone)
        const freeAmount = product.detail.bandwidth.options[0]

        if (!foundZone) {
            zone = "*"
        }

        const bwAmount = product.detail.bandwidthAdditionalByZone[zone][currency]

        return (+bwAmount * (+bandwidthEstimate - +freeAmount)).toFixed(2)
    }

    /**
     * Returns the cost of additional disk space above that of what is included
     * in the server cost (where applicable).
     *
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The server zone/location e.g. 'us-east-1'
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {number} diskSpace The amount of extra disk space in GB
     * @param {string} currency E.g. 'GBP'
     * @param {boolean} isStackUser
     * @returns {number} The extra disk space cost to two decimal places
     */
    additionalDiskPrice(
        spec,
        zone,
        provider,
        diskSpace,
        currency,
        isStackUser = false,
        hostshopCurrency = null
    ) {
        let product
        if (this instanceof ResellerCloudProductHelper && !isStackUser) {
            // Get the product from the base config (20i JSON)
            product = this.cloudProduct(provider, spec, true)
        } else {
            // We're using the standard CloudProductHelper so carry on as usual
            product = this.cloudProduct(provider, spec)
        }
        product = this.priceFix(product, hostshopCurrency ?? currency)

        if (product) {
            const disk = product.detail.disk

            if (
                diskSpace < disk.range.min ||
                diskSpace > disk.range.max
            ) {
                return 0
            }

            const availableZones = Object.keys(product.detail.diskAdditionalByZone)

            if (Array.isArray(availableZones) && availableZones.length) {
                const foundZone = availableZones.find(z => z == zone)

                if (!foundZone) {
                    zone = "*"
                }
            } else {
                return 0
            }

            const freeGbs = +product.detail.disk.range.min
            let diskAmountPerGb

            if (
                isStackUser &&
                hostshopCurrency
            ) {
                diskAmountPerGb = +product.detail.diskAdditionalByZone[zone][hostshopCurrency]
                return Math.round((+diskAmountPerGb * (+diskSpace - +freeGbs)) * 100) / 100
            }

            diskAmountPerGb = +product.detail.diskAdditionalByZone[zone][currency]

            if (
                isStackUser &&
                "_diskMarkup" in product.detail.diskAdditionalByZone[zone]
            ) {
                const type = product.detail.diskAdditionalByZone[zone]["_diskMarkup"].type
                const amount = +product.detail.diskAdditionalByZone[zone]["_diskMarkup"].amount

                if (type === "percent") {
                    // FIXME sanity check this when supported
                    diskAmountPerGb = diskAmountPerGb * (1 + (amount / 100))
                } else if (type === "fixed") {
                    diskAmountPerGb += amount
                }
            }

            return Math.round((+diskAmountPerGb * (+diskSpace - +freeGbs)) * 100) / 100
        }

        return 0
    }


    /**
     * Return an object with all pricing information.
     *
     * @param {string} provider
     * @param {string} spec
     * @param {(string | number)} diskSpaceAdditional
     * @param {(string | number)} predictedBandwidth
     * @param {string} zone
     * @param {string} billingPeriod
     * @param {string} currency
     * @param {boolean} isReseller
     * @returns {{yearlyDiscount: number, serverBasePrice: number,
     * preDiscountCodeserverBasePrice: number, discountCodeDescription: ?string,
     * additionalDiskPrice: number, predictedBandwidthPrice: number,
     * serverConfiguredPrice: number}}
     */
    allPricing(
        provider,
        spec,
        diskSpaceAdditional,
        predictedBandwidth,
        zone = "*",
        billingPeriod = "monthly",
        currency = "GBP",
        isReseller = false,
        isStackUser = false,
        hostshopCurrency = null,
        optimisationProfile = null
    ) {
        let serverBasePrice = +this.serverBasePrice(
            provider,
            spec,
            zone,
            currency,
            isReseller,
            isStackUser,
            hostshopCurrency,
            false,
            optimisationProfile
        )

        let discountCode = this.hasValidDiscount(provider,spec,zone,currency,optimisationProfile)
        let discountCodeDescription = discountCode ? discountCode.description : null
        let preDiscountCodeserverBasePrice = +this.serverBasePrice(
            provider,
            spec,
            zone,
            currency,
            isReseller,
            isStackUser,
            hostshopCurrency,
            discountCode ? true : false,
            optimisationProfile
        )

        let additionalDiskPrice = +this.additionalDiskPrice(
            spec,
            zone,
            provider,
            +diskSpaceAdditional,
            currency,
            isStackUser
        )

        let predictedBandwidthPrice = +this.predictedBandwidthPrice(
            spec,
            zone,
            provider,
            +predictedBandwidth,
            currency,
            isStackUser
        )

        let serverConfiguredPrice = serverBasePrice + additionalDiskPrice

        // Yearly discount not currently supported for HS
        let discount = isStackUser ? 0 : +this.yearlyDiscount(
            provider,
            spec,
            zone,
            currency,
            isReseller
        )

        if (billingPeriod == "yearly") {
            serverBasePrice = ((preDiscountCodeserverBasePrice * 11)+(+serverBasePrice.toFixed(2))) - discount
            additionalDiskPrice = (additionalDiskPrice * 12)
            predictedBandwidthPrice = (predictedBandwidthPrice * 12)
            preDiscountCodeserverBasePrice = (preDiscountCodeserverBasePrice * 12) - discount
            serverConfiguredPrice = serverBasePrice + additionalDiskPrice
        }

        serverBasePrice = Math.round(serverBasePrice * 100) / 100
        additionalDiskPrice = Math.round(additionalDiskPrice * 100) / 100
        predictedBandwidthPrice = Math.round(predictedBandwidthPrice * 100) / 100
        serverConfiguredPrice = Math.round(serverConfiguredPrice * 100) / 100
        preDiscountCodeserverBasePrice = Math.round(preDiscountCodeserverBasePrice * 100) / 100

        return {
            yearlyDiscount: discount,
            serverBasePrice,
            preDiscountCodeserverBasePrice,
            discountCodeDescription,
            additionalDiskPrice,
            predictedBandwidthPrice,
            serverConfiguredPrice
        }
    }

    /**
     * Return the zones organised into continents with the human-friendly label.
     *
     * @param {string} provider
     * @param {string} spec
     * @returns {string[]} Zone labels sorted into continents
     */
    availableContinents(provider, spec) {
        const sortOrder = [
            "North America",
            "Europe",
            "Asia & Oceania",
            "South America",
            "Middle East",
            "Asia"
        ]

        const zones = this.availableZones(provider, spec).map(
            zone => ServerLocationUtils.uiFriendlyContinentLabel(zone)
        )

        return [...new Set(zones)].sort((a, b) => {
            return sortOrder.indexOf(a) - sortOrder.indexOf(b)
        })
    }

    /**
     * Return the available zone options for a given cloud product.
     *
     * @param {string} provider
     * @param {string} spec
     * @returns {string[]} Zone codes
     */
    availableZones(provider, spec) {
        let zones = this.cloudProduct(provider, spec).detail.zone.options.filter(zone => {
            return !this.settings.disabledZones[provider].includes(zone)
        })

        if (provider == 'gcp' && spec == 'small') {
            return zones.filter(z => ![
                'northamerica-south',
                'southamerica-west1',
                'us-east5',
                'us-south1',
                'europe-north2',
                'europe-southwest1',
                'europe-west10',
                'europe-west12',
                'europe-west8',
                'europe-west9',
                'me-central1',
                'me-central2',
                'me-west1',
                'africa-south1',
            ].includes(z))
        }

        return zones
    }

    /**
     * Return the available zone options for a given cloud.
     *
     * @param {string} provider
     * @returns {string[]} Zone codes
     */
    availableZonesForCloud(provider, filterHidden = false) {
        let zones = []

        let cloudProducts = [];
        if (filterHidden) {
            // Used for the edit instances modal, shows cloud product options
            // from the resellers' config
            cloudProducts = this.cloudProductOptions(provider, true);
        } else {
            if (this.base) {
                // Used for the edit regiosn modal, needs to fetch zones from
                // base to allow reseller to toggle zones we offer
                provider = (provider === "resellerBrand" ? "20i" : provider);
                cloudProducts = Object.values(this.base.productData[provider]);
            }
        }

        for (const product of cloudProducts) {
            zones.push(...product.detail.zone.options)
        }

        const sortedZones = [...new Set(zones)]

        if (filterHidden) {
            return sortedZones.filter(zone => {
                return !this.settings.disabledZones[provider].includes(zone)
            })
        }

        return sortedZones
    }

    /**
     * Return the cloud product object
     *
     * @param {string} provider
     * @param {string} spec
     * @returns {Object} Cloud product object
     */
    cloudProduct(provider, spec, base = false) {
        if (base && this.base !== null) {
            if (provider === "resellerBrand") provider = "20i"
            return this.base.productData[provider][spec]
        }

        return this.config?.[provider][spec]
    }

    /**
     * Return the available cloud products for a given cloud.
     *
     * @param {string} cloud The cloud provider code e.g. 'aws'
     * @returns {Object[]} Available cloud products
     */
    cloudProductOptions(cloud, ignoreDiscontinued = false) {
        if (ignoreDiscontinued) {
            return Object.values(this.config[cloud])
        }

        if (this.settings?.discontinuedProducts[cloud]?.length) {
            return Object.values(this.config[cloud]).filter(spec => {
                return !this.settings.discontinuedProducts[cloud].includes(spec.specName)
            })
        }

        if (this.config && this.config[cloud]) {
            return Object.values(this.config[cloud])
        }

        return []
    }

    /**
     * @param {string} cloud
     * @param {boolean} darkMode
     * @returns {string} UI icon for the cloud provider
     */
    cloudProviderIcon(cloud, darkMode = false, active = false) {
        if (cloud === "resellerBrand") {
            // @ts-ignore
            if (this.brand && this.brand.hasCloudBrandLogo) {
                // @ts-ignore
                return `/cloudBrandLogo?${Math.random()}`
            }
        }

        return `/assets/images/server-cloud-providers/${cloud}${darkMode ? `-dm` : active ? `-white` : ``}.svg`
    }

    /**
     * @param {string} cloud
     * @param {boolean} darkMode
     * @returns {string} UI icon for the cloud provider
     */
    cloudIcon(cloud, darkMode = false, active = false) {
        if (cloud === "resellerBrand") return `/cloudBrandLogo?${Math.random()}`

        return `/assets/images/server-cloud-providers/${cloud}${darkMode ? `-dm` : active ? `-white` : ``}.svg`
    }

    /**
     * @param {string} cloud
     * @returns {string} UI label for the cloud provider
     */
    cloudProviderLabel(cloud) {
        // @ts-ignore
        const resellerBrand = this.brand && this.brand.cloudBrandName ?
            // @ts-ignore
            this.brand.cloudBrandName : "Your Cloud Brand"

        return ({
            '20i': '20iCloud',
            'aws': 'Amazon AWS',
            'gcp': 'Google Cloud Platform',
            'resellerBrand': resellerBrand
        })[cloud]
    }

    /**
     * @param {string} cloud
     * @returns {string} UI label for the cloud provider
     */
    static cloudLabel(cloud) {
        return ({
            '20i': '20iCloud',
            'aws': 'Amazon AWS',
            'gcp': 'Google Cloud Platform',
        })[cloud]
    }

    /** @param {string} cloud */
    cloudLabel(cloud) {
        return CloudProductHelper.cloudLabel(cloud)
    }

    /**
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @returns {string}
     */
    cpuLabel(provider, spec) {
        const product = this.cloudProduct(provider, spec)

        if (product) {
            const cores = product.detail.cpu.options[0]
            return `${cores} Core`
        }

        return ""
    }

    /**
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @returns {string}
     */
    ramLabel(provider, spec) {
        const product = this.cloudProduct(provider, spec)

        if (product) {
            const ram = product.detail.ram.options[0]
            return `${ram}GB RAM`
        }

        return ""
    }

    /**
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @returns {string}
     */
    ssdLabel(provider, spec) {
        const product = this.cloudProduct(provider, spec)

        if (product) {
            const disk = product.detail.disk.range.min
            return `${disk}GB SSD`
        }

        return ""
    }

    /**
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @returns {string}
     */
    bandwidthLabel(provider, spec) {
        const product = this.cloudProduct(provider, spec)
        if (product) {
            const bandwidth = product.detail.bandwidth.options[0]
            return `${this.formatBandwidth(bandwidth)} Bandwidth`
        }

        return ""
    }

    /**
     * Format a bandwidth allowance for display. Whole thousands of GB read
     * better as terabytes -- "1TB", "2TB" rather than "1000GB", "2000GB" --
     * so they are converted; anything else keeps the product's own figure and
     * unit untouched.
     *
     * @param {(string | number)} amount The allowance, in `uiLabel` units.
     * @param {string} [uiLabel] The product's unit label. Defaults to "GB",
     *     which is what every cloud product currently carries.
     * @returns {string}
     */
    formatBandwidth(amount, uiLabel) {
        const unit = uiLabel || "GB"
        const gb = +amount

        if (unit.toUpperCase() === "GB" && gb >= 1000 && gb % 1000 === 0) {
            return `${gb / 1000}TB`
        }

        return `${amount}${unit}`
    }

    /**
     * Return the maximum allowed GB of additional disk space for a given server
     * for a given cloud provider.
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @returns {number} The max allowed additional disk space in GB
     */
    diskRangeMax(provider, spec) {
        return +this.cloudProduct(provider, spec).detail.disk.range.max
    }

    /**
     * Return the minimum allowed GB of additional disk space for a given server
     * for a given cloud provider.
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @returns {number} The min allowed additional disk space in GB
     */
    diskRangeMin(provider, spec) {
        return +this.cloudProduct(provider, spec).detail.disk.range.min
    }

    /**
     * Return the cloud product basket label.
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @returns {string} Cloud product basket label
     */
    getBasketLabel(provider, spec) {
        const product = this.cloudProduct(provider, spec)
        let basketLabel = this.cloudProduct(provider, spec).basketLabel

        if (provider === "resellerBrand") {
            basketLabel = basketLabel.replace("20iCloud", this.cloudProviderLabel(provider))
        }

        if (
            basketLabel.toLowerCase().includes(product.specName) &&
            product.uiLabel !== product.specName.toLowerCase()
        ) {
            basketLabel = basketLabel.replace(
                product.specName.charAt(0).toUpperCase() + product.specName.slice(1),
                product.uiLabel
            )
        }

        return basketLabel
    }

    /**
     * Return Managed Price Spec
     *
     * @param {Object} spec
     * @returns {Object}
     */
    getManagedPriceSpec(spec) {
        if( this.isSpecManaged(spec) ){
            return this.settings['globalMarkupPricing'][spec.provider];
        } else {
            return null;
        }
    }

    /**
     * Return the optmisiation profile object for a given profile ID.
     *
     * @param {string} profileId
     * @returns {Object} Optimisation profile object
     */
    getOptimisationProfileObject(profileId) {
        return Object.values(this.optimisationProfiles).find(
            opProfile => opProfile.id == profileId
        )
    }

    /**
     * Check if configurable disk space is available for a given cloud product.
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @returns {boolean}
     */
    hasConfigurableDiskSpace(provider, spec = null) {
        if (spec === null) {
            return (provider === 'aws' || provider === 'gcp')
        }

        const prod = this.cloudProduct(provider, spec)

        if (prod) {
            if (
                prod.detail.disk.range.min ==
                prod.detail.disk.range.max
            ) {
                return false
            }

            return true
        }

        return false
    }

    /**
     * Returns true if the cloud provider has a pay as you go bandwidth option.
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @returns {boolean}
     */
    hasPayAsYouGoBandwidth(provider) {
        return (provider === 'aws' || provider === 'gcp')
    }

    /**
     * @returns {ServerLocationUtils}
     */
    get locationUtils() {
        return ServerLocationUtils
    }

    /**
     * Return true or false depending on if this spec should be using managed pricing.
     *
     * @returns {boolean}
     */
    isSpecManaged(spec) {
        const prod = this.cloudProduct(spec.provider, spec.spec)

        if (
            prod &&
            prod.detail &&
            prod.detail.priceByZone[spec.zone] &&
            prod.detail.priceByZone[spec.zone]['_globalMarkupPricing']
        ) {
            return true
        }

        return false
    }

    /**
     * With the current config this will find the lowest possible price across
     * all products and return a SBProductPricing object.
     *
     * @returns {?SBProductPricing}
     */
    lowestPriceSB() {
        if( this.config && !this.lowestPriceSBCache ){
            var lowestPrice = null;
            var providers = Object.keys(this.config);
            for(const provider of providers){
                var specs = Object.keys(this.config[provider]);
                for(const s of specs){
                    var zones = this.config[provider][s]['detail']['zone']['options'];

                    // Skip products that aren't for sale
                    if (
                        this.settings.discontinuedProducts[provider].includes(s)
                    ) {
                        continue;
                    }

                    for(const z of zones){
                        if (
                            this.settings.disabledZones[provider].includes(z)
                        ) {
                            continue;
                        }
                        let p = this.serverBasePrice(
                            provider,
                            s,
                            z,
                            (UserCatalogue.inst).currency,
                            false,
                            true,
                            (UserCatalogue.inst).currency,
                            false,
                            null,
                        )

                        if (!lowestPrice || (lowestPrice && p < lowestPrice)) {
                            lowestPrice = p
                        }
                    }
                }
            }
            this.lowestPriceSBCache = new SBProductPricing({
                action: null,
                isManaged: false,
                periodMonths: 1,
                paymentType: "any",
                price: lowestPrice
            });
        }
        return this.lowestPriceSBCache;
    }

    /**
     * Used for the bandwidth sliders on the build server pages. Hard coded for
     * the time being as all applicable server options in the config JSON file just have a
     * min amount of 2 and no other information (value is used for predictions only).
     *
     * @todo Make dynamic
     *
     * @returns {number}
     */
    predictedBandwidthMax() {
        return 5000
    }

    /**
     * Used for the bandwidth sliders on the build server pages. Hard coded for
     * the time being as all applicable server options in the config JSON file just have a
     * min amount of 2 and no other information (value is used for predictions only).
     *
     * @todo Make dynamic
     *
     * @returns {number}
     */
    predictedBandwidthMin() {
        return 2
    }

    /**
     * Returns the estimated bandwidth monthly cost based on provider &
     * currency.
     *
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The server zone/location e.g. 'us-east-1'
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {number} bandwidthEstimate The estimated bandwidth usage in GB
     * @param {string} currency E.g. 'GBP'
     * @returns {number} The estimated monthly bandwidth cost to two decimal places
     */
    predictedBandwidthPrice(
        spec,
        zone,
        provider,
        bandwidthEstimate,
        currency,
        isStackUser = false,
        hostshopCurrency = null
    ) {
        let product
        if (this instanceof ResellerCloudProductHelper && !isStackUser) {
            // Get the product from the base config (20i JSON)
            product = this.cloudProduct(provider, spec, true)
        } else {
            // We're using the standard CloudProductHelper so carry on as usual
            product = this.cloudProduct(provider, spec)
        }

        if (product) {
            const availableZones = Object.keys(product.detail.bandwidthAdditionalByZone)

            if (Array.isArray(availableZones) && availableZones.length) {
                const foundZone = availableZones.find(z => z == zone)

                if (!foundZone) {
                    zone = "*"
                }
            } else {
                return 0
            }

            const freeGbs = +product.detail.bandwidth.options[0]
            let bwAmountPerGb

            if (
                isStackUser &&
                hostshopCurrency
            ) {
                bwAmountPerGb = +product.detail.bandwidthAdditionalByZone[zone][hostshopCurrency]
                return Math.round((+bwAmountPerGb * (+bandwidthEstimate - +freeGbs)) * 100) / 100
            }

            bwAmountPerGb = +product.detail.bandwidthAdditionalByZone[zone][currency]

            if (
                isStackUser &&
                "_bandwidthMarkup" in product.detail.bandwidthAdditionalByZone[zone]
            ) {
                const type = product.detail.bandwidthAdditionalByZone[zone]["_bandwidthMarkup"].type
                const amount = +product.detail.bandwidthAdditionalByZone[zone]["_bandwidthMarkup"].amount

                if (type === "percent") {
                    // FIXME sanity check this when supported
                    bwAmountPerGb = bwAmountPerGb * (1 + (amount / 100))
                } else if (type === "fixed") {
                    bwAmountPerGb += amount
                }
            }

            return Math.round((+bwAmountPerGb * (+bandwidthEstimate - +freeGbs)) * 100) / 100
        }

        return 0
    }

    /**
     * Given a full cloud server spec return the full price and sanitised spec.
     *
     * A JS version of what basket does.
     *
     * @param {object} spec Cloud server spec
     * @param {string} billingPeriod Either "monthly" or "yearly"
     * @param {string} currency E.g. "GBP"
     * @param {boolean} isReseller
     * @param {boolean} isStackUser Price this as a stack user is billed,
     *     matching \Stack\Product\Traits\CloudProduct in
     *     customer-information-service: the reseller's markup applies to the
     *     reseller price (list price less the reseller discount), not to the
     *     list price, and no yearly discount is offered.
     * @returns {{ price: number, fullSpec: object }}
     */
    priceForSpec(
        spec,
        billingPeriod = "monthly",
        currency      = "GBP",
        isReseller    = false,
        isStackUser   = false
    ) {
        if( !this.config[spec.provider] ){
            throw new Error(`Provider ${spec.provider} not found`);
        }

        // Resolved once, up front: the spec is mutated further down (missing
        // fields are filled in with defaults), so re-testing it later could
        // give a different answer to the one the markup was derived from.
        var isManagedSpec = this.isSpecManaged(spec);

        var multiplyer,managedAmount;
        if( isManagedSpec ){
            var managedPriceSpec = this.getManagedPriceSpec(spec);
            var managedType      = (managedPriceSpec['type'] == "percent" ? "percent" : "amount");
            if( managedType == 'percent' ){
                multiplyer       = ((100 + (+managedPriceSpec['amount'])) / 100);
                managedAmount    = 0.00;
            } else {
                multiplyer       = 1;
                managedAmount    = +managedPriceSpec['amount'];
            }
        } else {
            multiplyer       = 1;
            managedAmount    = 0.00;
        }

        var thisSpec = JSON.parse(
            JSON.stringify(
                ( isManagedSpec ? this.base.productData["20i"][spec.spec]["detail"] : this.config[spec.provider][spec.spec]["detail"])
            )
        );
        if( isManagedSpec ){
            thisSpec.provider.options.push("resellerBrand");
        }

        if( !thisSpec ){
            throw new Error(`No spec found`);
        }

        var optionPrice = 0;
        for(const specName of CloudProductHelper.specNames()){
            if( thisSpec[specName]["options"] && !Array.isArray(thisSpec[specName]["options"]) ){
                thisSpec[specName]["options"] = Object.keys(thisSpec[specName]["options"]);
            }

            if( spec[specName] ){
                if(
                    thisSpec[specName]["options"] &&
                    !thisSpec[specName]["options"].includes(spec[specName]+'')
                ) {
                    throw new Error(`Invalid ${specName} '${spec[specName]}' for cloud service`);
                } else if (
                    thisSpec[specName]["range"] &&
                    (
                        +spec[specName] < thisSpec[specName]["range"]["min"] ||
                        +spec[specName] > thisSpec[specName]["range"]["max"]
                    )
                ) {
                    throw new Error(`Invalid range '${specName}'`);
                } else if ( thisSpec[specName]["range"] ) {
                    var pastMin = +spec[specName] - +thisSpec[specName]["range"]["min"];
                    if( pastMin > 0 ){
                        switch(specName){
                            case 'disk':
                                if( thisSpec.diskAdditionalByZone[spec.zone] ){
                                    optionPrice+=(+thisSpec.diskAdditionalByZone[spec.zone][currency] * +pastMin);
                                } else if ( thisSpec.diskAdditionalByZone["*"] ) {
                                    optionPrice+=(+thisSpec.diskAdditionalByZone["*"][currency] * +pastMin);
                                } else if (thisSpec[specName]["range"]["price"]) {
                                    optionPrice+=(+thisSpec[specName]["range"]["price"][currency] * +pastMin);
                                } else {
                                }
                                break;
                            default:
                                optionPrice+=(+thisSpec[specName]["range"]["price"][currency] * +pastMin);
                        }
                    }
                }
            } else if ( thisSpec[specName]["options"] && thisSpec[specName]["options"].length == 1 ) {
                spec[specName] = thisSpec[specName]["options"][0];
            } else if ( thisSpec[specName]["range"] ){
                spec[specName] = thisSpec[specName]["range"]["min"];
            } else {
                throw new Error(`Missing required spec`);
            }
        }

        var priceByZone;
        if( !thisSpec['priceByZone'][spec['zone']] ){
            priceByZone = +thisSpec['priceByZone']["*"][currency];
        } else {
            priceByZone = +thisSpec['priceByZone'][spec['zone']][currency];
        }

        // A managed spec is sold on by the reseller, so the markup below has
        // to be applied to the reseller price rather than to our list price.
        if( isReseller || (isStackUser && isManagedSpec) ){
            priceByZone = priceByZone * this.resellerDiscount
        }

        var basePrice = ((priceByZone + managedAmount) * multiplyer) + optionPrice;

        var discount;
        switch( billingPeriod ){
            case 'yearly':
                if( isStackUser ){
                    // Stack users are not offered the yearly discount; the
                    // renewal is billed as twelve months at the monthly price.
                    discount = 0;
                } else if( thisSpec['yearlyDiscountByZone'][spec['zone']] ){
                    if (isReseller) {
                        discount = +thisSpec['yearlyDiscountByZone'][spec['zone']][currency] * this.resellerDiscount
                    } else {
                        discount = +thisSpec['yearlyDiscountByZone'][spec['zone']][currency];
                    }
                } else if ( thisSpec['yearlyDiscountByZone']["*"] ) {
                    if (isReseller) {
                        discount = +thisSpec['yearlyDiscountByZone']["*"][currency] * this.resellerDiscount
                    } else {
                        discount = +thisSpec['yearlyDiscountByZone']["*"][currency];
                    }
                } else {
                    discount = 0;
                }
                return {
                    price: (basePrice * 12) - discount,
                    fullSpec: spec
                };
                break;
            default:
                return {
                    price: basePrice,
                    fullSpec: spec
                }
        }
    }

    /**
     * Get first matching valid discount
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The server zone e.g. 'eu-west-1' ('*' for all zones)
     * @param {string} currency The currency code e.g. 'GBP'
     * @return {Object} The discount code object
     */
    hasValidDiscount(provider,spec,zone,currency,optimisationProfile) {
        if(
            this.discountData &&
            this.discountData[provider]
        ) {
            let now = new Date();
            for(const dcode of Object.keys(this.discountData[provider])){
                if(
                    this.discountData[provider][dcode].validSpecs.includes(spec) &&
                    this.discountData[provider][dcode].valididProfiles.includes(optimisationProfile)
                ) {
                    // Spec is valid
                    let startTime = new Date(this.discountData[provider][dcode]["startTime"]);
                    let endTime = new Date(this.discountData[provider][dcode]["endTime"]);
                    if( now >= startTime && now <endTime ){
                        // Valid time frame
                        if( this.discountData[provider][dcode]["discount"][currency] ){
                            // Valid currency
                            // This discount will be applied.
                            let result = this.discountData[provider][dcode]["discount"][currency];
                            result.description = this.discountData[provider][dcode]['description'];
                            return result;
                        }
                    }
                }
            }
        }
    }

    /**
     * Get the server price without additional config (e.g. disk space)
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The server zone e.g. 'eu-west-1' ('*' for all zones)
     * @param {string} currency The currency code e.g. 'GBP'
     * @returns {number} The server price before additional configuration
     */
    serverBasePrice(
        provider,
        spec,
        zone,
        currency,
        isReseller = false,
        isStackUser = false,
        hostshopCurrency = null,
        noDiscount = false,
        optimisationProfile = null
    ) {
        let codeDiscount;
        if( !noDiscount ){
            codeDiscount = this.hasValidDiscount(provider,spec,zone,currency,optimisationProfile)
        }

        let prod
        if (isReseller && this instanceof ResellerCloudProductHelper) {
            // Get the product from the base config (20i JSON)
            prod = this.cloudProduct(provider, spec, true)
        } else {
            // We're using the standard CloudProductHelper so carry on as usual
            prod = this.cloudProduct(provider, spec)
        }

        let pricing = {}
        if (prod && prod.detail.priceByZone) {
            if (zone in prod.detail.priceByZone) {
                pricing = prod.detail.priceByZone[zone]
            } else {
                pricing = prod.detail.priceByZone['*']
            }
        }

        if (currency in pricing) {
            // If is a reseller, return the price with any reseller discount
            // applied
            if (isReseller) {
                const resellerDiscount = +this.resellerDiscount ? +this.resellerDiscount : 1
                let price = (+pricing[currency] * resellerDiscount)
                if( codeDiscount ){
                    if( codeDiscount.type == "percent" ){
                        let amount = (price * (1 - (+codeDiscount.amount/100)))
                        return Math.floor(price - amount) + 0.99;
                    } else if ( codeDiscount.type == "fixed" ){
                        return price - (+codeDiscount.amount)
                    }
                } else {
                    return price;
                }
            }

            // If is a stack user we need to determine how this product has been
            // priced...
            if (isStackUser) {
                // Order of precedence:

                // If alternative currency (HostShop currency is different to
                // that the reseller uses in 20i), return price[currency].
                // @ts-ignore
                if (hostshopCurrency && hostshopCurrency != currency) {
                    return +pricing[hostshopCurrency]
                }

                // If not an alternative currency, check if global markup is
                // used for this product. If it is, find the base (20i) price
                // and apply the global markup to it.
                if (this.isSpecManaged({ spec: spec, provider: provider, zone: zone })) {
                    let priceWithGlobalMarkup = 0
                    const markup = this.getManagedPriceSpec({
                        spec: spec, provider: provider, zone: zone
                    })

                    let baseProduct

                    if (provider === "resellerBrand") {
                        if (this.base) {
                        baseProduct = this.base.productData["20i"][spec]
                            baseProduct = this.base.productData["20i"][spec]
                            baseProduct = this.base.productData["20i"][spec]
                        } else {
                            baseProduct = this.cloudProduct(provider, spec)
                        }
                    } else {
                        if (this.base) {
                            baseProduct = this.base.productData[provider][spec]
                        } else {
                            baseProduct = this.cloudProduct(provider, spec)
                        }
                    }

                    let basePrice = 0
                    let basePriceWithResellerDiscount = 0
                    if (baseProduct) {
                        if (baseProduct.detail.priceByZone[zone]) {
                            basePrice = +baseProduct.detail.priceByZone[zone][currency]
                        } else {
                            basePrice = +baseProduct.detail.priceByZone["*"][currency]
                        }

                        basePriceWithResellerDiscount = basePrice * this.resellerDiscount
                    } else {
                        basePrice = +pricing[currency]
                        basePriceWithResellerDiscount = basePrice * this.resellerDiscount
                    }

                    if (markup.type === "percent") {
                        priceWithGlobalMarkup = basePriceWithResellerDiscount * (1 + (+markup.amount / 100))
                    } else if (markup.type === "fixed") {
                        priceWithGlobalMarkup = basePriceWithResellerDiscount + +markup.amount
                    }

                    return priceWithGlobalMarkup
                }

                // If not an alternative currency and not using global markup,
                // check if product markup is used for this product. If it is,
                // apply the product markup to the price.
                if ("_markupPricing" in pricing) {
                    let priceWithProductMarkup = 0
                    /** @type {{ type: string, amount: (string | number) }} */
                    // @ts-ignore
                    const markup = pricing._markupPricing

                    let baseProduct

                    if (provider === "resellerBrand") {
                        baseProduct = this.base.productData["20i"][spec]
                    } else {
                        baseProduct = this.base.productData[provider][spec]
                    }

                    let basePrice
                    if (baseProduct.detail.priceByZone[zone]) {
                        basePrice = +baseProduct.detail.priceByZone[zone][currency]
                    } else {
                        basePrice = +baseProduct.detail.priceByZone["*"][currency]
                    }

                    const basePriceWithResellerDiscount = basePrice * this.resellerDiscount

                    if (markup.type === "percent") {
                        priceWithProductMarkup = +basePriceWithResellerDiscount * (1 + (+markup.amount / 100))
                    } else if (markup.type === "fixed") {
                        priceWithProductMarkup = (+basePriceWithResellerDiscount + +markup.amount)
                    }

                    return priceWithProductMarkup
                }

                // If not an alternative currency, not using global markup and
                // not using product markup, pass this and return
                // +pricing[currency] as normal.
                return +pricing[currency]
            }

            if( codeDiscount ){
                if( codeDiscount.type == "percent" ){
                    let amount = (pricing[currency] * (1 - (+codeDiscount.amount/100)))
                    return pricing[currency] - amount
                } else if ( codeDiscount.type == "fixed" ){
                    return pricing[currency] - (+codeDiscount.amount)
                }
            }

            return +pricing[currency]
        }

        return 0
    }

    /**
     * Gets the base price of a server and sends back 15%
     * for the timeline backups cost.
     *
     * @param {Object} conf
     * @param {string} period
     * @param {string} currency
     * @returns {string}
     */
    timelineBackupsAddOnPrice(conf, period, currency, diskAdditional, hostshopCurrency = "GBP") {
        var price = this.allPricing(
            conf.provider,
            conf.spec,
            diskAdditional,
            conf.bandwidth,
            conf.zone,
            period,
            currency,
            false,
            true,
            hostshopCurrency,
        )

        return (
            +price.serverConfiguredPrice * this.settings.timelinePercentPrice ?? 0.15
        ).toFixed(2)
    }

    /**
     * Given a contract that has its own spec (current server) and an upgrade
     * spec this will calculate the price now to perform an upgrade on a prorata.
     *
     * If a currency is not provided GBP is default.
     *
     * @param {object} contract The contract object
     * @param {object} upgradeSpec The upgrade spec object
     * @param {string} currency
     * @returns {string} The upgrade price pro-rata
     */
    upgradePrice(
        contract,
        upgradeSpec,
        currency = "GBP",
        reseller = false
    ) {
        // @ts-ignore
        var upgradeSpec = this.serverBasePrice(
            upgradeSpec.provider,
            upgradeSpec.spec,
            upgradeSpec.zone,
            currency,
            reseller
        )

        var currentSpec = this.serverBasePrice(
            contract.basketProductOptions?.provider ?? contract.infoCache.provider,
            contract.basketProductOptions?.spec ?? contract.infoCache.spec,
            contract.basketProductOptions?.zone ?? contract.infoCache.zone,
            currency,
            reseller
        )
        return this.upgradeProRata(
            currentSpec,
            upgradeSpec,
            contract.renewalPeriod ?? (contract.preferredRenewalMonths == 12 ? "yearly" : "monthly"),
            contract.expiryDate
        );
    }

    /**
     * Get the price of a server upgrade for the remaining period.
     *
     * A JS version of what basket does.
     *
     * @param {string | number} old_price
     * @param {string | number} new_price
     * @param {string} period Either "monthly" or "yearly"
     * @param {Date | string} expiry_date End data for the period
     * @param {?Date | string} current_date
     * @returns {string} The upgrade price pro-rata
     */
    upgradeProRata(
        old_price,
        new_price,
        period,
        expiry_date,
        current_date = null
    ) {
        var e   = new Date(expiry_date);
        var now = current_date ? new Date(current_date) : new Date();

        var days_left = (
            (e.valueOf()/1000) - (now.valueOf()/1000)
        ) / 86400;

        var days_in_period = null;
        switch(period){
            case 'yearly':
                days_in_period = 366;
                break;
            case 'monthly':
                days_in_period = 31;
                break;
            default:
                throw new Error("Unsupported period")
        }
        // @ts-ignore
        var sum = (new_price - old_price) * days_left / days_in_period;
        if( sum < 0 ){
            return "0.00";
        } else {
            return (Math.round( sum * 2 ) / 2).toFixed(2);
        }
    }

    /**
     * Return the yearly discount amount for a given cloud product.
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The server zone e.g. 'eu-west-1' ('*' for all zones)
     * @param {string} currency The currency code e.g. 'GBP'
     * @param {bool} true if is reseller
     * @returns {number} Yearly discount amount
     */
    yearlyDiscount(
        provider,
        spec,
        zone,
        currency = "GBP",
        isReseller = false
    ) {
        const prod = this.cloudProduct(provider, spec)
        let resellerDiscount = 1;
        if (isReseller) {
            resellerDiscount = +this.resellerDiscount ? +this.resellerDiscount : 1
        }

        if (prod) {
            if (
                prod.detail.yearlyDiscountByZone[zone] &&
                prod.detail.yearlyDiscountByZone[zone][currency]
            ) {
                return +prod.detail.yearlyDiscountByZone[zone][currency] * resellerDiscount
            }

            if (
                '*' in prod.detail.yearlyDiscountByZone &&
                prod.detail.yearlyDiscountByZone['*'][currency]
            ) {
                return +prod.detail.yearlyDiscountByZone['*'][currency] * resellerDiscount
            }

            return 0
        }

        return 0
    }

    /**
     * Return the yearly discount label for a given cloud product, if
     * applicable.
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The server zone e.g. 'eu-west-1' ('*' for all zones)
     * @param {string} currency The currency code e.g. 'GBP'
     * @returns {string} Yearly discount label
     */
    yearlyDiscountLabel(
        provider,
        spec,
        zone,
        currency
    ) {
        const discount = this.yearlyDiscount(provider, spec, zone, currency)

        if (discount && discount > -1) {
            if (this.settings['yearlyDiscountLabel']['*']) {
                return this.settings['yearlyDiscountLabel']['*']
            }

            if (
                this.settings['yearlyDiscountLabel'] &&
                this.settings['yearlyDiscountLabel'][currency]
            ) {
                return this.settings['yearlyDiscountLabel'][currency]
            }

            return ''
        }

        return ''
    }

    /**
     * For some users whose intial products weren't generated correctly
     *
     * @param {Object} obj
     * @param {string} [currency]
     */
    priceFix(obj,currency) {
        if (typeof obj !== 'object' || obj === null) {
            return obj;
        }

        const newObj = Array.isArray(obj) ? [] : {};

        for (const key in obj) {
            const newKey = key === "null" ? currency : key;

            newObj[newKey] = this.priceFix(obj[key], currency);
        }

        return newObj;
    }
}

/** Class to assist with cloud products JSON schema (Reseller specific) */
class ResellerCloudProductHelper extends CloudProductHelper {
    /**
     * Load the returned cloud product data JSON file into the class
     *
     * @param {Object} config
     */
    constructor(config, brand = null) {
        super(config,brand)
        this.brand = brand
        this.base = null
    }

    /**
     * Check if a given server has been discontinued by the reseller
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} specName The server spec e.g. 'micro'
     * @returns {boolean}
     */
    isDiscontinued(provider, specName) {
        const isDiscontinued = this.settings.discontinuedProducts[provider].indexOf(specName)

        if (isDiscontinued > -1) {
            return true
        }

        return false
    }

    /**
     * Delete any price keys for null or "" to allow cis to populate.
     */
    sanitisePricing() {
        for(const provider of Object.keys(this.config)){
            for(const spec of Object.keys(this.config[provider])){
                for(const zone of Object.keys(this.config[provider][spec]['detail']['priceByZone'])){
                    for(const currency of Object.keys(this.config[provider][spec]['detail']['priceByZone'][zone])){
                        if( !this.config[provider][spec]['detail']['priceByZone'][zone][currency] ){
                            delete this.config[provider][spec]['detail']['priceByZone'][zone][currency];
                        }
                    }
                }
            }
        }
    }

    /**
     * Save the Reseller CPH config object
     *
     * @param {boolean} whmcs
     * @param {boolean} quiet Skip the success notification, for saves the
     * reseller didn't ask for
     * @returns {Promise}
     */
    async saveConfig(whmcs = false, quiet = false) {
        this.sanitisePricing();
        const url = whmcs == false ? `/a/catalogue/cloudProducts` : `/a/whmcs/cloud-products`;

        let result = await $.ajax({
            url,
            method: "post",
            data: JSON.stringify({
                productData: this.config,
                settings: this.settings,
            }),
            contentType: "application/json",
        })
            .then(() => {
                if (!quiet) {
                    ShowNotification.success("Saved successfully.")
                }
            })
            .catch(error => {
                console.error(error)
                const errorMessage = "An error occured. Please try again or contact support."
                ShowNotification.fail(errorMessage)
            })

        var cloudProducts = await $.ajax(url);
        this.config       = cloudProducts.productData;

        return true;
    }

    /**
     * Adds a new zone that has been added to the reseller product data.
     *
     * @param {string} zone The zone to be added.
     * @throws {Error}
     */
    addNewZone(zone) {
        if (!zone || typeof zone !== 'string') {
            throw new Error('Zone must be a non-empty string');
        }

        if (!this.base || !this.base.productData) {
            throw new Error('Base product data not available');
        }

        // Find which cloud provider this zone belongs to
        let targetProvider = null;
        for (const provider in this.base.productData) {
            for (const spec in this.base.productData[provider]) {
                const baseProduct = this.base.productData[provider][spec];
                if (baseProduct.detail?.zone?.options?.includes(zone)) {
                    targetProvider = provider;
                    break;
                }
            }
            if (targetProvider) break;
        }

        if (!targetProvider) {
            throw new Error(`Zone '${zone}' not found in base product data`);
        }

        const providersToUpdate = [targetProvider];
        if (targetProvider === '20i' && this.config['resellerBrand']) {
            providersToUpdate.push('resellerBrand');
        }

        // Update products for each relevant provider
        for (const providerToUpdate of providersToUpdate) {
            if (this.config[providerToUpdate]) {
                for (const spec in this.config[providerToUpdate]) {
                    const resellerProduct = this.config[providerToUpdate][spec];
                    const baseProduct = this.cloudProduct(targetProvider, spec, true);

                    // Skip if base product doesn't exist or doesn't have the zone
                    if (!baseProduct || !baseProduct.detail?.zone?.options?.includes(zone)) {
                        continue;
                    }

                    if (resellerProduct.detail?.zone?.options) {
                        if (!resellerProduct.detail.zone.options.includes(zone)) {
                            resellerProduct.detail.zone.options.push(zone);
                        }
                    }

                    const pricingTypes = [
                        'priceByZone',
                        'diskAdditionalByZone',
                        'bandwidthAdditionalByZone',
                        'yearlyDiscountByZone'
                    ];

                    // Copy pricing data for the new zone from base to reseller config
                    for (const pricingType of pricingTypes) {
                        if (baseProduct.detail[pricingType]?.[zone]) {
                            if (!resellerProduct.detail[pricingType]) {
                                resellerProduct.detail[pricingType] = {};
                            }

                            // Copy the zone pricing data from base to reseller config
                            resellerProduct.detail[pricingType][zone] = JSON.parse(
                                JSON.stringify(baseProduct.detail[pricingType][zone])
                            );
                        }
                    }
                }
            }
        }
    }

    /**
     * Discontinue a product/mark not for sale
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @returns {Promise}
     */
    async discontinue(provider, spec) {
        if (!this.isDiscontinued(provider, spec)) {
            this.settings.discontinuedProducts[provider].push(spec)
            return this.saveConfig()
        }

        throw new Error("Cloud Product is already discontinued.")
    }

    /**
     * Mark a product for sale/remove from discontinued
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @returns {Promise}
     */
    async markForSale(provider, spec) {
        if (this.isDiscontinued(provider, spec)) {
            const index = this.settings.discontinuedProducts[provider].indexOf(spec)
            this.settings.discontinuedProducts[provider].splice(index, 1)
            return this.saveConfig()
        }

        throw new Error("Cloud Product is already for sale.")
    }

    /**
     * Used on initial save when the reseller begins the process of selling
     * cloud products. We make a copy of the 20i JSON file but set all of the
     * product prices to the reseller discounted amount so the reseller starts
     * selling with a markup of 0.00 and can configure from there. If the
     * resellers' Hostshop is in another currency it will use that currency with
     * the same amounts, ready for the reseller to configure pricing.
     *
     * resellerCloudProductHelper#saveConfig() is called in the Vue instance
     * after calling this.
     *
     * @param {Object} productData The product data (config), passed as a
     * parameter so we can recursively iterate over the object.
     * @param {string} resellers20iCurrency The reseller's 20i currency e.g.
     * 'GBP' NOT the HostShop configured currency
     * @param {string} resellersHostshopCurrency The reseller's HostShop
     * currency e.g. 'HKD' NOT the resellers' currency within 20i
     * @param {string} eRate The exchange rate to use
     */
    async setInitialPricing(
        productData,
        resellers20iCurrency,
        resellersHostshopCurrency,
        eRate
    ) {
        for (const key in productData) {
            if (typeof productData[key] === 'object' && productData[key] !== null) {
                await this.setInitialPricing(
                    productData[key],
                    resellers20iCurrency,
                    resellersHostshopCurrency,
                    eRate
                )
            } else {
                if (key === resellers20iCurrency) {
                    // Store the amount
                    let amount = productData[key]

                    // We have grabbed the amount so we'll clear out all of the
                    // initial values and just have the one amount in the
                    // resellers' HS currency
                    if ("USD" in productData) {
                        delete productData["USD"]
                    }
                    if ("GBP" in productData) {
                        delete productData["GBP"]
                    }

                    if( resellers20iCurrency != resellersHostshopCurrency && eRate ){
                        productData[resellersHostshopCurrency] = (+amount) * (+eRate);
                    } else {
                        productData[resellersHostshopCurrency] = amount
                    }
                }
            }
        }
    }

    /**
     * Create a default reseller cloud brand ready for customisation
     */
    async createCloudBrand() {
        const resellerBrand = JSON.parse(JSON.stringify(this.config['20i']))

        this.applyCloudBrandToProducts(resellerBrand)

        this.config.resellerBrand = resellerBrand
    }

    /**
     * Point a copy of 20i's cloud product data at the resellers' own cloud
     * brand rather than 20iCloud. Mutates the object given, which can be a
     * single product or a whole set of them.
     *
     * @param {Object} obj
     */
    applyCloudBrandToProducts(obj) {
        if (typeof obj !== 'object' || obj === null) {
            return
        }

        if (Array.isArray(obj)) {
            obj.forEach(item => this.applyCloudBrandToProducts(item))
            return
        }

        if ("basketLabel" in obj) {
            if (obj.basketLabel.includes("20iCloud")) {
                obj.basketLabel = obj.basketLabel.split("20i")[1]
            }
        }

        if (obj.detail && obj.detail.provider && obj.detail.provider.options) {
            obj.detail.provider.options = obj.detail.provider.options.map(option =>
                option === '20i' ? 'resellerBrand' : option
            )
        }

        for (const key in obj) {
            this.applyCloudBrandToProducts(obj[key])
        }
    }

    /**
     * @important
     *
     * The resellers' config is a copy of 20i's cloud product data taken at the
     * point they first set up Managed Cloud Servers (see the page level
     * initialSave()). Anything 20i adds after that - a new instance size, or a
     * whole new cloud provider - would never appear in their config, so they
     * could never sell it.
     *
     * This merges in anything 20i offers (the base config) that the reseller
     * doesn't have. New products are priced exactly as they would have been on
     * first setup and are marked as discontinued, so they show on the Cloud
     * Servers page but are NOT for sale until the reseller has checked the
     * pricing and switched them on.
     *
     * Requires #base to be set. #saveConfig() should be called afterwards if
     * anything was merged.
     *
     * @param {string} resellers20iCurrency The reseller's 20i currency e.g.
     * 'GBP' NOT the HostShop configured currency
     * @param {string} resellersHostshopCurrency The reseller's HostShop
     * currency e.g. 'HKD' NOT the resellers' currency within 20i
     * @param {?(string | number)} eRate The exchange rate to use, only needed
     * when the two currencies differ
     * @returns {Promise<{provider: string, spec: string}[]>} The products merged in
     */
    async mergeNewBaseProducts(
        resellers20iCurrency,
        resellersHostshopCurrency,
        eRate = null
    ) {
        if (
            !this.base ||
            !this.base.productData ||
            !this.config ||
            !this.settings
        ) {
            return []
        }

        const merged = []

        for (const provider of Object.keys(this.base.productData)) {
            // Handled below - the resellers' own brand is a copy of 20i's
            // products, not a cloud provider in its own right.
            if (provider === "resellerBrand") {
                continue
            }

            merged.push(...await this.mergeNewBaseProductsFor(
                provider,
                provider,
                resellers20iCurrency,
                resellersHostshopCurrency,
                eRate
            ))
        }

        // Only resellers who already have a cloud brand get new products added
        // to it. Creating one for those who don't isn't our call to make.
        if (this.config.resellerBrand) {
            merged.push(...await this.mergeNewBaseProductsFor(
                "20i",
                "resellerBrand",
                resellers20iCurrency,
                resellersHostshopCurrency,
                eRate
            ))
        }

        return merged
    }

    /**
     * Merge the products 20i offers for a single cloud provider into the
     * resellers' config, leaving any they already have alone.
     *
     * @param {string} baseProvider The provider key in the base config e.g.
     * '20i'
     * @param {string} configProvider The provider key in the resellers' config.
     * The same as baseProvider unless we're populating their cloud brand.
     * @param {string} resellers20iCurrency
     * @param {string} resellersHostshopCurrency
     * @param {?(string | number)} eRate
     * @returns {Promise<{provider: string, spec: string}[]>} The products merged in
     */
    async mergeNewBaseProductsFor(
        baseProvider,
        configProvider,
        resellers20iCurrency,
        resellersHostshopCurrency,
        eRate = null
    ) {
        const baseProducts = this.base.productData[baseProvider]

        if (!baseProducts) {
            return []
        }

        const existingProducts = this.config[configProvider] ?? {}
        const newSpecNames = Object.keys(baseProducts).filter(
            specName => !(specName in existingProducts)
        )

        if (!newSpecNames.length) {
            return []
        }

        this.config[configProvider] = existingProducts
        this.initialiseProviderSettings(configProvider, baseProvider)

        for (const specName of newSpecNames) {
            const product = JSON.parse(JSON.stringify(baseProducts[specName]))

            if (configProvider === "resellerBrand") {
                this.applyCloudBrandToProducts(product)
            }

            await this.setInitialPricing(
                product,
                resellers20iCurrency,
                resellersHostshopCurrency,
                eRate
            )

            this.config[configProvider][specName] = product

            if (!this.settings.discontinuedProducts[configProvider].includes(specName)) {
                this.settings.discontinuedProducts[configProvider].push(specName)
            }
        }

        this.sortProductsToBaseOrder(baseProvider, configProvider)

        return newSpecNames.map(specName => ({
            provider: configProvider,
            spec: specName,
        }))
    }

    /**
     * List a cloud provider's products in the same order 20i lists them, so a
     * newly merged instance sits between the sizes either side of it rather
     * than on the end. Anything the reseller has that 20i no longer offers
     * keeps its position at the end.
     *
     * @param {string} baseProvider The provider key in the base config
     * @param {string} configProvider The provider key in the resellers' config
     */
    sortProductsToBaseOrder(baseProvider, configProvider) {
        const baseProducts = this.base.productData[baseProvider]
        const products = this.config[configProvider]
        const sorted = {}

        for (const specName of Object.keys(baseProducts)) {
            if (specName in products) {
                sorted[specName] = products[specName]
            }
        }

        for (const specName of Object.keys(products)) {
            if (!(specName in sorted)) {
                sorted[specName] = products[specName]
            }
        }

        this.config[configProvider] = sorted
    }

    /**
     * Give the settings object the entries a cloud provider needs, for when one
     * has only just appeared in the resellers' config. Seeded from 20i's
     * settings where possible so a new provider starts out configured the same
     * way a fresh HostShop setup would be - its products are held back by
     * discontinuedProducts rather than by these.
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} baseProvider The provider key to seed from in the base
     * config
     */
    initialiseProviderSettings(provider, baseProvider = provider) {
        const baseSettings = (this.base && this.base.settings) ? this.base.settings : {}

        for (const key of ["discontinuedProducts", "disabledZones"]) {
            if (!this.settings[key]) {
                this.settings[key] = {}
            }

            if (!Array.isArray(this.settings[key][provider])) {
                this.settings[key][provider] = []
            }
        }

        // 20i doesn't have every one of these for every provider - disk markup
        // for example only applies to the clouds with configurable disk space.
        for (const key of [
            "globalMarkupPricing",
            "diskMarkupPerGb",
            "bandwidthMarkupPerGb",
        ]) {
            if (!baseSettings[key] || !baseSettings[key][baseProvider]) {
                continue
            }

            if (!this.settings[key]) {
                this.settings[key] = {}
            }

            if (!this.settings[key][provider]) {
                this.settings[key][provider] = JSON.parse(
                    JSON.stringify(baseSettings[key][baseProvider])
                )
            }
        }

        // The instances modal reads the global markup for the cloud it is
        // editing, so this one has to be there whatever the base config says.
        if (!this.settings.globalMarkupPricing) {
            this.settings.globalMarkupPricing = {}
        }

        if (!this.settings.globalMarkupPricing[provider]) {
            this.settings.globalMarkupPricing[provider] = {
                amount: 0,
                type: "percent",
            }
        }

        if (
            Array.isArray(baseSettings.offersAnnualPricing) &&
            baseSettings.offersAnnualPricing.includes(baseProvider) &&
            Array.isArray(this.settings.offersAnnualPricing) &&
            !this.settings.offersAnnualPricing.includes(provider)
        ) {
            this.settings.offersAnnualPricing.push(provider)
        }
    }

    /**
     * Return the markup object for the given type, provider, spec and zone
     *
     * @param {string} type One of 'server', 'disk' or 'bandwidth'
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The zone e.g. 'eu-west-1'
     * @returns
     */
    getProductMarkup(type, provider, spec, zone) {
        const product = this.cloudProduct(provider, spec)

        if (
            product &&
            product.detail &&
            product.detail.priceByZone &&
            product.detail.priceByZone[zone] &&
            product.detail.priceByZone[zone]['_markupPricing']
        ) {
            const serverMarkup = this.config[provider][spec].detail.priceByZone[zone]['_markupPricing']
            const diskMarkup = this.config[provider][spec].detail.diskAdditionalByZone[zone]['_markupPricing']
            const bandwidthMarkup = this.config[provider][spec].detail.bandwidthAdditionalByZone[zone]['_markupPricing']

            return ({
                server: serverMarkup,
                disk: diskMarkup,
                bandwidth: bandwidthMarkup
            })[type]
        }

        return {
            amount: 0,
            type: 'fixed', // or 'percent'
        }
    }

    /**
     * @param {string} resellerCurrency Three letter currency code e.g. 'GBP'
     * and the currency the reseller's 20i account is set in
     * @param {string} hostshopCurrency Three letter currency code e.g. 'CAD'
     * and the currency the resellers' HostShop is set in
     * @returns {boolean}
     */
    usesAlternativeCurrency(resellerCurrency, hostshopCurrency) {
        return resellerCurrency !== hostshopCurrency
    }

    /**
     * Change the price of a cloud product.
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The zone e.g. 'eu-west-1'
     * @param {(string | number)} amount The amount to set the price to
     * @param {string} currency The currency to set the price in
     */
    editPrice(
        provider,
        spec,
        zone,
        amount,
        currency
    ) {
        let pricing

        if (this.config[provider][spec].detail.priceByZone[zone]) {
            pricing = this.config[provider][spec].detail.priceByZone[zone]
        } else {
            pricing = this.config[provider][spec].detail.priceByZone["*"]
        }

        if ("_markupPricing" in pricing) {
            delete pricing["_markupPricing"]
        }

        pricing[currency] = amount
    }

    /**
     * Change the markup price of a cloud product.
     *
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The zone e.g. 'eu-west-1'
     * @param {(string | number)} amount The amount to set the price to
     * @param {string} type The markup type e.g. 'fixed' or 'percent'
     */
    editMarkup(
        provider,
        spec,
        zone,
        amount,
        type = "fixed"
    ) {
        this.config[provider][spec].detail.priceByZone[zone]['_markupPricing'] = {}
        this.config[provider][spec].detail.priceByZone[zone]['_markupPricing'].amount = amount
        this.config[provider][spec].detail.priceByZone[zone]['_markupPricing'].type = type
    }

    /**
     * Set the bandwidth amount
     *
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The zone e.g. 'eu-west-1'
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {(string | number)} amount The amount
     * @param {string} currency The currency
     */
    setBandwidthAmount(
        spec,
        zone,
        provider,
        amount,
        currency
    ) {
        const pricing = this.config[provider][spec].detail.bandwidthAdditionalByZone[zone]

        pricing[currency] = `${amount}`
    }

    /**
     * Set the bandwidth markup amount
     *
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The zone e.g. 'eu-west-1'
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {(string | number)} amount The markup amount
     * @param {string} currency The currency
     * @param {string} type The markup type e.g. 'fixed' or 'percent'
     */
    setBandwidthMarkup(
        spec,
        zone,
        provider,
        amount,
        currency,
        type = "fixed"
    ) {
        const pricing = this.config[provider][spec].detail.bandwidthAdditionalByZone[zone]

        pricing["_bandwidthMarkup"] = {
            amount,
            type,
        }
    }

    /**
     * Get the bandwidth markup amount.
     *
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The zone e.g. 'eu-west-1'
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} currency The currency
     * @returns
     */
    getBandwidthMarkup(
        spec,
        zone,
        provider,
        currency
    ) {
        const pricing = this.config[provider][spec].detail.bandwidthAdditionalByZone[zone]

        if (pricing && "_bandwidthMarkup" in pricing) {
            return pricing["_bandwidthMarkup"]
        }

        return {
            amount: 0,
            type: "fixed", // or 'percent'
        }
    }

    /**
     * Set the disk markup amount
     *
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The zone e.g. 'eu-west-1'
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {(string | number)} amount The markup amount
     * @param {string} currency The currency
     */
    setDiskAmount(
        spec,
        zone,
        provider,
        amount,
        currency
    ) {
        const pricing = this.config[provider][spec].detail.diskAdditionalByZone[zone]

        pricing[currency] = `${amount}`
    }

    /**
     * Set the disk markup amount
     *
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The zone e.g. 'eu-west-1'
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {(string | number)} amount The markup amount
     * @param {string} currency The currency
     * @param {string} type The markup type e.g. 'fixed' or 'percent'
     */
    setDiskMarkup(
        spec,
        zone,
        provider,
        amount,
        currency,
        type = "fixed"
    ) {
        const pricing = this.config[provider][spec].detail.diskAdditionalByZone[zone]

        pricing["_diskMarkup"] = {
            amount,
            type,
        }
    }

    /**
     * Get the disk markup amount.
     *
     * @param {string} spec The server spec e.g. 'micro'
     * @param {string} zone The zone e.g. 'eu-west-1'
     * @param {string} provider The cloud provider e.g. 'aws'
     * @param {string} currency The currency
     * @returns
     */
    getDiskMarkup(
        spec,
        zone,
        provider,
        currency
    ) {
        const pricing = this.config[provider][spec].detail.diskAdditionalByZone[zone]

        if ("_diskMarkup" in pricing) {
            return pricing["_diskMarkup"]
        }

        return {
            amount: 0,
            type: "fixed", // or 'percent'
        }
    }
}
